// src/com/poi_info/MapPoiInfo.js
import React, { useState } from 'react';
import { styled  } from '@mui/material/styles';
import { Box, CircularProgress, IconButton, Popover } from '@mui/material';
import DrawIcon from '@mui/icons-material/Draw';
import StartIcon from '@mui/icons-material/Start';
import RefreshIcon from '@mui/icons-material/Refresh';
import KeyboardTabIcon from '@mui/icons-material/KeyboardTab';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline'; // 도움말
import { AppNumber, AppObject, AppStyle, AppWord, MapValue } from '../../value/AppConst';
import { useReactiveVar } from '@apollo/client';
import { useSnackbar } from 'notistack';
import SearchBox from '../../common/SearchBox';
import { authTableTreeRepo, mapPoiAllListRepo } from '../../value/AppModel';
import { MapPoiAllDictionary } from './MapPoiAllDict';
import FlexyTable from '../../common/flexytable/FlexyTable';
import { useAuthorizationCheck } from '../../api/app-hook';
import Util from '../../value/Util';
import { getFloatingInfoBoxHeight } from '../map/MapFloatingInfo';

const PoiInfoListContainer = styled(Box)({
	flexGrow:1, display:'flex', flexDirection:'column'
});

const HeaderBox = styled(Box)({
    display:'flex', alignItems:'center', //backgroundColor:'white',
    height: AppNumber.HeaderToolBoxHeight, padding: AppNumber.SmallBoxPadding,
    //boxSizing:'border-box'
});

const FloatingInfoOption = {
    width: 520, // box width
    // rowHeight: 40,
};
const FloatingInfoProps = MapPoiAllDictionary.floatingInfoProps;


export default function MapPoiInfo({
    mapShell,
    onRequestOpenEditor,
    responsePoiList,
    onRequestRefresh,
    onMouseEnterIndexCell
}) {
    // const [poiInfoSelected, setPoiInfoSelected] = useState(null);
    const authTableTree = useReactiveVar(authTableTreeRepo);
    const mapPoiAllListData = useReactiveVar(mapPoiAllListRepo);
    const [searchText, setSearchText] = useState('');
    const checkAuth = useAuthorizationCheck();
    const { enqueueSnackbar, closeSnackbar } = useSnackbar();
    const [helpAnchorEl, setHelpAnchorEl] = useState(null);

    const floatingInfoBoxHeight = getFloatingInfoBoxHeight(FloatingInfoProps, FloatingInfoOption);

    const onChangeSearchText = (text) => {
        setSearchText(text);
    };

    const onMouseEnterTableIndexCell = (item, e) => {
        if(!item) onMouseEnterIndexCell(null);
        else {
            const windowHeight = window.innerHeight - 10;
            let y = e.clientY; // + AppNumber.MapHeaderHeight;
            if(y + floatingInfoBoxHeight > windowHeight) {
                y = windowHeight - floatingInfoBoxHeight;
            }
            onMouseEnterIndexCell(item, FloatingInfoProps, FloatingInfoOption, y);
        }
    };

    const onPoiRecordClicked = (item) => {
        // setPoiInfoSelected(item);
        if(item.lat && item.lon)
            mapShell.morphTo(item.lat, item.lon, MapValue.Level.MAX_FOR_CLUSTER+1);
        else enqueueSnackbar(item.poiName + ' 지점의 위치(위도, 경도)가 설정되어 있지 않습니다', {variant:'warning', autoHideDuration:3000});
    };

    const onClickToolOnRow = (toolId, row) => {
        switch(toolId) {
            case AppObject.command.SET_ZONE:
                onRequestOpenEditor(row);
                break;
            case AppObject.command.CMD_SET_START:
                Util.bubbleNaverRoutePoint({isStart:true, data:{lat:row.lat, lon:row.lon, address: row.addr}});
                break;
            case AppObject.command.CMD_SET_END:
                Util.bubbleNaverRoutePoint({isStart:false, data:{lat:row.lat, lon:row.lon, address: row.addr}});
                break;
            default: return;
        }
    };

    const canEditDcZone = checkAuth(authTableTree.tree.pr_dc_info_set_zone);
    const canEditStoreZone = checkAuth(authTableTree.tree.pr_store_info_set_zone);
    const tools = [
        {
            id:AppObject.command.SET_ZONE,
            icon: <DrawIcon/>,
            title: '구역 설정',
            where: (row) => {
                if(row.poiGroup === 'DC') return canEditDcZone;
                else return canEditStoreZone;
            }
        },
        {id:AppObject.command.CMD_SET_START, icon:<StartIcon fontSize={AppWord.SMALL} color={AppStyle.mui.success}/>, title:'경로탐색 시작점으로 설정'},
        {id:AppObject.command.CMD_SET_END, icon: <KeyboardTabIcon fontSize={AppWord.SMALL}color={AppStyle.mui.success} />, title:'경로탐색 종점으로 설정'}
    ];


    return(
        <PoiInfoListContainer>
            <HeaderBox>
                {
                    responsePoiList.loading ?
                    <CircularProgress size={20} /> :
                    <IconButton onClick={onRequestRefresh}>
                        <RefreshIcon/>
                    </IconButton>
                }
                <Box flexGrow={1}>
                    <SearchBox
                        value={searchText}
                        onChangeText={onChangeSearchText}
                        width={140}
                    />
                </Box>
                <IconButton
                    title='도움말'
                    color={AppStyle.mui.primary}
                    onClick={(e)=>setHelpAnchorEl(e.currentTarget)}
                >
                    <HelpOutlineIcon />
                </IconButton>
                <Popover
                    open={Boolean(helpAnchorEl)}
                    anchorEl={helpAnchorEl}
                    onClose={()=>setHelpAnchorEl(null)}
                    anchorOrigin={{vertical:'bottom', horizontal:'center'}}
                    transformOrigin={{vertical:'top', horizontal:'center'}}
                >
                    <Box
                        style={{
                            padding:10, fontSize:'0.85rem', borderRadius:5,
                            border: '1px solid ' + AppStyle.PrimaryRGB,
                        }}
                    >
                        <p>지점목록의 각 행으로 마우스를 옮기면 행의 오른쪽에 아이콘들이 나타납니다.</p>
                        {
                            canEditDcZone &&
                            <p>
                                <DrawIcon style={{marginRight:20}}
                                    color={AppStyle.mui.primary}
                                />
                                DC 또는 점포의 출발, 도착 감지구역과 위치를 설정하는 상자를 엽니다.
                            </p>}
                        <p>
                            <StartIcon style={{marginRight:20}}
                                color={AppStyle.mui.primary}
                            />
                            선택한 지점의 위치를 경로탐색 출발지로 설정합니다.
                            상단에 탐색을 위한 상자가 나타납니다.
                        </p>
                        <p>
                            <KeyboardTabIcon style={{marginRight:20}}
                                color={AppStyle.mui.primary}
                            />
                            선택한 지점의 위치를 경로탐색 도착지로 설정합니다.
                        </p>

                    </Box>
                </Popover>
            </HeaderBox>
            <FlexyTable
                name={MapPoiAllDictionary.tableProps.tableId}
                tableProps={MapPoiAllDictionary.tableProps}
                records={mapPoiAllListData.records}
                search={searchText}
                tools={tools}
                onClickTool={onClickToolOnRow}
                onRowClicked={onPoiRecordClicked}
                onEnterIndex={onMouseEnterTableIndexCell}
            />
        </PoiInfoListContainer>
    );
}